// Admin user roles and permissions
export type AdminRole =
  | 'SUPER_ADMIN'
  | 'BILLING_ADMIN'
  | 'BILLING_VIEWER'
  | 'SUPPORT_ADMIN';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  roles?: AdminRole[];
  permissions: string[];
  lastLogin?: string;
  mfaEnabled: boolean;
}

export interface AuthState {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Role-based permission mappings
export const ROLE_PERMISSIONS: Record<AdminRole, string[]> = {
  SUPER_ADMIN: ['*'],
  BILLING_ADMIN: [
    'billing:read',
    'billing:write',
    'billing:delete',
    'audit:read',
    'config:read',
    'config:write',
    'bookings:read',
    'bookings:write',
    'content:read',
    'content:write',
    'support:read',
    'support:write',
    'users:read',
    'trips:read',
  ],
  BILLING_VIEWER: ['billing:read', 'audit:read', 'config:read', 'support:read'],
  SUPPORT_ADMIN: ['support:read', 'support:write', 'users:read', 'audit:read'],
};

export function hasPermission(
  user: AdminUser | null,
  permission: string
): boolean {
  if (!user) return false;
  if (!permission) return false;
  // Check for wildcard permission (SUPER_ADMIN has ["*"])
  if (user.permissions.includes('*')) return true;
  return user.permissions.includes(permission);
}

export function hasAnyPermission(
  user: AdminUser | null,
  permissions: string[]
): boolean {
  if (!user) return false;
  if (permissions.length === 0) return false;
  // Check for wildcard permission (SUPER_ADMIN has ["*"])
  if (user.permissions.includes('*')) return true;
  return permissions.some(p => user.permissions.includes(p));
}

export function hasAllPermissions(
  user: AdminUser | null,
  permissions: string[]
): boolean {
  if (!user) return false;
  // Check for wildcard permission (SUPER_ADMIN has ["*"])
  if (user.permissions.includes('*')) return true;
  return permissions.every(p => user.permissions.includes(p));
}
